import {
  AllAttributesType,
  analyticsMap,
  MetricsType,
  ValueTypes,
  WidgetDisplayOptions,
  allAttrsEnum,
} from '@/services/api/resources/analytics-builder/constants';

type NullableMetric = MetricsType | null;
type NullableAttribute = AllAttributesType | null;

export const useAnalyticsMap = () => {
  const getLineChartGroupOptions = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return [];
    }

    return analyticsMap.get(metric)
      ?.enabledAttributes
      .get(attribute)
      ?.enabledLineChartTimeGroupings ?? [];
  };

  const containsAttribute = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return false;
    }

    return analyticsMap.get(metric)?.enabledAttributes.has(attribute) ?? false;
  };

  const getEnabledMetricValueTypes = (metric: NullableMetric) => {
    if (!metric) {
      return [];
    }

    return analyticsMap.get(metric)?.enabledValueTypes ?? [];
  };

  const getEnabledValueTypes = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return [];
    }

    return analyticsMap.get(metric)
      ?.enabledAttributes
      .get(attribute)
      ?.enabledValueTypes ?? [];
  };

  const isMetricOnlyRatioValues = (metric: NullableMetric) => {
    if (!metric) {
      return false;
    }

    const valueTypes = getEnabledMetricValueTypes(metric);

    return valueTypes.length === 1 && valueTypes[0] === ValueTypes.ratio;
  };

  const isOnlyAbsoluteValues = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return false;
    }

    const valueTypes = getEnabledValueTypes(metric, attribute);

    return valueTypes.length === 1 && valueTypes[0] === ValueTypes.absolute;
  };

  const isRatioValuesTypeEnabled = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return false;
    }

    return getEnabledValueTypes(metric, attribute).includes(ValueTypes.ratio);
  };

  const isAllChartsEnabled = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return false;
    }

    const displays = analyticsMap.get(metric)?.enabledAttributes.get(attribute)?.enabledDisplays;

    if (!displays) {
      return false;
    }

    return displays.includes(WidgetDisplayOptions.barChart) && displays.includes(WidgetDisplayOptions.lineChart);
  };

  const isAllChartsDisabled = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return true;
    }

    const displays = analyticsMap.get(metric)?.enabledAttributes.get(attribute)?.enabledDisplays;

    if (!displays) {
      return false;
    }

    return !displays.includes(WidgetDisplayOptions.barChart) && !displays.includes(WidgetDisplayOptions.lineChart);
  };

  const getDefaultDisplay = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return null;
    }

    if (attribute === allAttrsEnum.response) {
      return null;
    }

    const display = analyticsMap.get(metric)?.enabledAttributes.get(attribute)?.defaultDisplay;

    if (!display) {
      return null;
    }

    return display;
  };

  const getEnabledDisplays = (metric: NullableMetric, attribute: NullableAttribute) => {
    if (!(metric && attribute)) {
      return [];
    }

    return analyticsMap.get(metric)
      ?.enabledAttributes
      .get(attribute)
      ?.enabledDisplays ?? [];
  };

  return {
    getLineChartGroupOptions,
    containsAttribute,
    getEnabledMetricValueTypes,
    getEnabledValueTypes,
    isMetricOnlyRatioValues,
    isOnlyAbsoluteValues,
    isRatioValuesTypeEnabled,
    isAllChartsEnabled,
    isAllChartsDisabled,
    getDefaultDisplay,
    getEnabledDisplays,
  };
};
